(function () {
"use strict";
const STORE = Object.freeze({
  origin: 'https://medicalstoreoc.com',
  phone: '+17148523414',
  phoneDisplay: '(714) 852-3414',
  address: '2959 Fairview Rd, Costa Mesa, CA 92626',
  rentalPath: 'pages/urgent-equipment-requests',
  contactPath: 'pages/contact',
  shopPath: 'pages/find-the-right-equipment-with-expert-guidance'
});

const CATEGORIES = Object.freeze([
  {id: 'scooters', collection: 'mobility-scooters', handles: ["go-go®-ultra-x-4-wheel-s49", "buzzaround-xl-4-wheel-model-gb152-xl", "buzzaround-carryon-model-gb120"]},
  {id: 'wheelchairs', collection: 'wheelchairs', handles: ["proactive-medical-chariot-iii-wheelchair-18-k3-wheelchair", "proactive-medical-chariot-ii-wheelchair", "noka-travelerlite-lightweight-transport-chair-5018bk"]},
  {id: 'walkers', collection: 'walkers-rollators', handles: ["getgo-petite-walker-blue-w-p-4208cbl", "noka-classic-steel-rollator-6-inch-wheels", "nova-folding-walker-w-5-wls-2-btn-std-4090dw5"]},
  {id: 'chairs', collection: 'lift-chairs', handles: ["vivalift-®-elegance-2-plr-975m", "cloud-model-pr511-lar-skd-cloud-large", "golden-technologies-titan-pr-448-infinite-position-lift-chair"]},
  {id: 'beds', collection: 'hospital-beds', handles: ["tuffcare-century-long-term-care-bed-t7036", "costcare-long-term-care-low-profile-bed-bc320", "drive-game-changer-ultra-light-plus-semi-electric-hospital-bed-with-mattress-and-rails"]},
  {id: 'bathroom', collection: 'bathroom-safety', handles: ["nova-bath-chair-9016", "rose-healthcare-3-raised-toilet-seat-open-front", "nova-sliding-swivel-transfer-bench-9076"]}
]);

const STRINGS = Object.freeze({
  launcher: 'Find equipment', title: 'Your equipment guide', subtitle: 'Medical Store · Costa Mesa',
  disclosure: 'Automated shopping assistant', privacy: 'No personal or medical information is requested or saved by this guide.',
  intro: 'What brings you in today?', introText: 'Find equipment to buy, explore rentals, or connect with our store team.',
  buy: 'Shop equipment', buyNote: 'Browse products and prices', rent: 'Explore rentals', rentNote: 'Check options with the team',
  contact: 'Talk to the store', contactNote: 'Get help with your equipment questions',
  category: 'What would you like to browse?', categoryText: 'Choose a product category. This guide compares shopping choices, not medical suitability.',
  category_scooters: 'Mobility scooters', category_wheelchairs: 'Wheelchairs & transport chairs',
  category_walkers: 'Walkers & rollators', category_chairs: 'Lift chairs', category_beds: 'Hospital beds', category_bathroom: 'Bathroom safety',
  budget: 'Have a budget in mind?', budgetText: 'Optional. We’ll filter a small selection by its current listed starting price. Taxes and delivery may apply.',
  allBudgets: 'Show all price ranges', under: 'Up to {amount}', back: 'Back', restart: 'Start over', close: 'Close equipment guide',
  loading: 'Checking current product details…', results: 'Explore these options', resultsText: 'A small selection, with available-online listings first. Review options, fit, current price, and availability on the product page before buying.',
  live: 'Product details loaded from the store. Reopen this guide to refresh.', partial: 'Some products could not be checked. Browse the full category for more options.',
  unavailable: 'Current product details are unavailable here. Browse the store directly or ask the team for help.',
  noMatch: 'No items in this selection match that price range. The full category may have more options.',
  viewProduct: 'View product & options', browseCategory: 'Browse full category', seePrice: 'Ask the store for price',
  from: 'From {amount}', listed: 'Listed price {amount}', notAvailable: 'Not currently available online',
  priceNotice: 'Prices are product prices only. Final options, availability, taxes, and delivery are confirmed on the store website.',
  safety: 'For equipment suitability, sizing, or clinical needs, ask a qualified healthcare professional. This guide does not give medical advice.',
  rentalTitle: 'Let’s get your rental started', rentalText: 'The store can confirm rental availability, dates, pricing, pickup, and delivery. Opening the request page does not reserve equipment.',
  rentalAction: 'Open rental request page', rentalCategory: 'Rental interest: {category}', rentalNote: 'Tell the team which equipment you want and your preferred rental dates on the store’s own page. This guide does not send a request.',
  teamTitle: 'A real person can help', teamText: 'Contact the Medical Store team for product questions, availability, pickup, or delivery.',
  call: 'Call {phone}', visitContact: 'Open store contact page', emergency: 'For a medical emergency, call 911. This store is not an emergency medical service.',
  preview: 'Local preview · Not installed on the store', selection: 'Your selection', unknownCurrency: 'See product page for current pricing',
  retry: 'Check again', resultsReady: 'Showing {count} product options.', resultsNone: 'No matching products in this selection.', resultsUnavailable: 'Product details could not be checked. Store links are available.', resultsPartial: 'Showing {count} options. Some products could not be checked.'
});


function safeRoot(root = '/') {
  return typeof root === 'string' && /^\/(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_-]*$/.test(root) ? root.replace(/\/?$/, '/') : '/';
}

function validHandle(handle) {
  return typeof handle === 'string' && /^[\p{L}\p{N}][\p{L}\p{N}®_-]*$/u.test(handle) && handle.length <= 240;
}

function storeURL(path, root = '/') {
  const [kind, handle, extra] = path.split('/');
  if (!['products', 'collections', 'pages'].includes(kind) || !validHandle(handle) || extra !== undefined) throw new Error('Invalid store path');
  return new URL(safeRoot(root) + kind + '/' + encodeURIComponent(handle), STORE.origin).href;
}

function productURL(handle, root = '/') {
  return storeURL('products/' + handle, root);
}

function safeImage(value) {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value, STORE.origin);
    if (url.protocol !== 'https:' || !(url.hostname === 'cdn.shopify.com' || url.hostname === 'medicalstoreoc.com' || url.hostname.endsWith('.shopifycdn.com'))) return null;
    return url.href;
  } catch { return null; }
}

function normalizeProduct(raw, expectedHandle) {
  if (!raw || raw.handle !== expectedHandle || !validHandle(expectedHandle) || typeof raw.title !== 'string' || !raw.title.trim()) return null;
  const variants = Array.isArray(raw.variants) ? raw.variants : [];
  if (raw.requires_selling_plan === true || /\brental\b/i.test(raw.title) || variants.some(v => /\b(daily|weekly|monthly|rental|weeks|months)\b/i.test(v.title || ''))) return null;
  const available = variants.filter(v => v.available === true);
  const candidates = available.length ? available : variants;
  const prices = candidates.map(v => v.price).filter(p => Number.isSafeInteger(p) && p > 0);
  const hasUnknownPrice = candidates.length === 0 || candidates.some(v => !Number.isSafeInteger(v.price) || v.price <= 0);
  return Object.freeze({
    handle: expectedHandle, title: raw.title.slice(0, 240),
    image: safeImage(raw.featured_image),
    price: hasUnknownPrice || !prices.length ? null : Math.min(...prices),
    priceVaries: hasUnknownPrice || new Set(prices).size > 1,
    available: available.length > 0,
    checkedAt: Date.now()
  });
}

function selectProducts(products, budget = null, limit = 3) {
  const max = budget === null ? Infinity : Number(budget);
  if (!(max > 0)) return [];
  return products.filter(p => budget === null || (p.price !== null && p.price <= max))
    .sort((a, b) => Number(b.available) - Number(a.available) || (a.price ?? Infinity) - (b.price ?? Infinity) || a.title.localeCompare(b.title))
    .slice(0, Math.max(0, limit));
}

function money(amount, currency = 'USD', locale = 'en-US') {
  if (!Number.isSafeInteger(amount) || amount <= 0 || !/^[A-Z]{3}$/.test(currency)) return null;
  try { return new Intl.NumberFormat(locale, {style: 'currency', currency, currencyDisplay: 'code'}).format(amount / 100); }
  catch { return null; }
}

function formatText(template, values = {}) {
  return String(template).replace(/\{(\w+)\}/g, (match, key) => Object.hasOwn(values, key) ? String(values[key]) : match);
}

async function fetchCatalog(handles, {fetcher = globalThis.fetch, root = '/', preview = false, signal, timeoutMs = 8000} = {}) {
  const paths = [...new Set(handles)].filter(validHandle).slice(0, 12);
  let failed = 0;
  const products = await Promise.all(paths.map(async handle => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener('abort', abort, {once: true});
    const timeout = setTimeout(abort, timeoutMs);
    try {
      const path = preview ? '/api/products/' + encodeURIComponent(handle) + '.js' : safeRoot(root) + 'products/' + encodeURIComponent(handle) + '.js';
      const response = await fetcher(path, {signal: controller.signal, credentials: preview ? 'omit' : 'same-origin', headers: {Accept: 'application/json'}, cache: 'no-store'});
      if (!response.ok) throw new Error('Product unavailable');
      const product = normalizeProduct(await response.json(), handle);
      if (!product) throw new Error('Invalid product');
      return product;
    } catch { failed++; return null; }
    finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
  }));
  return {products: products.filter(Boolean), failed, checked: paths.length};
}


class MedicalStoreAssistant extends HTMLElement {
  connectedCallback() {
    if (this.shadowRoot) return;
    this.attachShadow({mode: 'open'});
    this.strings = {...STRINGS};
    try { Object.assign(this.strings, JSON.parse(this.querySelector('[data-msa-strings]')?.textContent || '{}')); } catch {}
    this.preview = this.dataset.preview === 'true';
    this.root = safeRoot(this.dataset.root || window.Shopify?.routes?.root || '/');
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || (this.preview ? 'USD' : '');
    this.locale = document.documentElement.lang || 'en-US';
    this.state = {step: 'home', intent: 'buy', category: null, budget: null};
    this.requestId = 0;
    this.abortController = null;
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href = this.dataset.styles || '/assets/medical-store-assistant.css';
    this.shadowRoot.append(style);
    this.launcher = this.el('button', 'launcher', this.t('launcher'));
    this.launcher.type = 'button';
    this.launcher.setAttribute('aria-haspopup', 'dialog');
    this.launcher.setAttribute('aria-expanded', 'false');
    this.launcher.setAttribute('aria-controls', 'msa-dialog');
    this.launcher.addEventListener('click', () => this.dialog.open ? this.close() : this.open());
    this.dialog = this.el('dialog', 'panel');
    this.dialog.id = 'msa-dialog';
    this.dialog.setAttribute('aria-labelledby', 'msa-title');
    const header = this.el('header', 'header');
    const brand = this.el('div', 'brand');
    brand.append(this.el('span', 'brand-mark', '+'));
    const words = this.el('div');
    const title = this.el('h2', '', this.t('title'));
    title.id = 'msa-title';
    words.append(title, this.el('p', 'subtitle', this.t('subtitle')));
    brand.append(words);
    const close = this.el('button', 'close', '×');
    close.type = 'button'; close.setAttribute('aria-label', this.t('close'));
    close.addEventListener('click', () => this.close());
    header.append(brand, close);
    this.main = this.el('div', 'content');
    this.status = this.el('p', 'status');
    this.status.setAttribute('role', 'status'); this.status.setAttribute('aria-live', 'polite');
    const footer = this.el('footer', 'footer');
    footer.append(this.el('span', 'automated', this.t('disclosure')), this.el('p', '', this.t('privacy')));
    this.dialog.append(header, this.main, this.status, footer);
    this.dialog.addEventListener('cancel', event => {event.preventDefault(); this.close();});
    this.dialog.addEventListener('keydown', event => {if (event.key === 'Escape') {event.preventDefault(); this.close();}});
    this.shadowRoot.append(this.launcher, this.dialog);
    this.render(false);
  }
  disconnectedCallback() { this.abortController?.abort(); this.requestId++; }
  t(key, values) { return formatText(this.strings[key] ?? STRINGS[key] ?? key, values); }
  el(tag, className = '', text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }
  button(label, action, className = 'option', note) {
    const button = this.el('button', className);
    button.type = 'button';
    button.append(this.el('span', 'label', label));
    if (note) button.append(this.el('span', 'option-note', note));
    button.addEventListener('click', action);
    return button;
  }
  link(label, url, className = 'action') {
    const link = this.el('a', className, label); link.href = url;
    link.rel = 'noopener noreferrer'; link.referrerPolicy = 'no-referrer';
    return link;
  }
  storeLink(label, path, className) { return this.link(label, storeURL(path, this.root), className); }
  open() {
    this.dialog.show(); this.launcher.setAttribute('aria-expanded', 'true');
    if (this.state.step === 'results') this.go('results');
    else this.focusHeading();
  }
  close() { this.dialog.close(); this.launcher.setAttribute('aria-expanded', 'false'); this.launcher.focus(); }
  focusHeading() { this.main.querySelector('h3')?.focus({preventScroll: true}); }
  go(step, patch = {}) {
    this.abortController?.abort(); this.requestId++;
    this.state = {...this.state, ...patch, step};
    this.render();
  }
  heading(key, textKey) {
    const heading = this.el('h3', '', this.t(key)); heading.tabIndex = -1;
    this.main.append(heading);
    if (textKey) this.main.append(this.el('p', 'intro', this.t(textKey)));
  }
  navigation(backStep) {
    const nav = this.el('nav', 'nav');
    nav.append(this.button('← ' + this.t('back'), () => this.go(backStep), 'text-button'));
    nav.append(this.button(this.t('restart'), () => this.go('home', {category: null, budget: null}), 'text-button'));
    this.main.append(nav);
  }
  contactLinks() {
    const group = this.el('div', 'stack');
    group.append(this.link(this.t('call', {phone: STORE.phoneDisplay}), 'tel:' + STORE.phone, 'action secondary'));
    group.append(this.storeLink(this.t('visitContact'), STORE.contactPath, 'text-link'));
    this.main.append(group);
  }
  render(focus = true) {
    this.main.replaceChildren(); this.status.textContent = ''; this.main.removeAttribute('aria-busy');
    const {step, intent, category} = this.state;
    if (step === 'home') {
      this.heading('intro', 'introText');
      const choices = this.el('div', 'stack');
      choices.append(this.button(this.t('buy'), () => this.go('category', {intent: 'buy'}), 'option featured', this.t('buyNote')));
      choices.append(this.button(this.t('rent'), () => this.go('category', {intent: 'rent'}), 'option', this.t('rentNote')));
      choices.append(this.button(this.t('contact'), () => this.go('contact'), 'option', this.t('contactNote')));
      this.main.append(choices);
    } else if (step === 'category') {
      this.navigation('home'); this.heading('category', 'categoryText');
      const grid = this.el('div', 'category-grid');
      for (const c of CATEGORIES) grid.append(this.button(this.t('category_' + c.id), () => this.go(intent === 'rent' ? 'rental' : 'budget', {category: c, budget: null})));
      this.main.append(grid);
    } else if (step === 'budget') {
      this.navigation('category'); this.heading('budget', 'budgetText');
      this.main.append(this.el('span', 'chip', this.t('category_' + category.id)));
      const choices = this.el('div', 'stack');
      if (this.currency === 'USD') {
        const budgets = ['scooters', 'chairs', 'beds'].includes(category.id) ? [100000, 200000, 400000] : [10000, 30000, 60000];
        for (const budget of budgets) choices.append(this.button(this.t('under', {amount: money(budget, this.currency, this.locale)}), () => this.go('results', {budget})));
      }
      choices.append(this.button(this.t('allBudgets'), () => this.go('results', {budget: null}), 'option featured'));
      this.main.append(choices);
    } else if (step === 'results') {
      this.navigation('budget'); this.heading('results', 'resultsText');
      this.main.append(this.el('span', 'chip', this.t('category_' + category.id)));
      this.resultList = this.el('div', 'products'); this.main.append(this.resultList);
      this.main.append(this.storeLink(this.t('browseCategory'), 'collections/' + category.collection, 'action secondary'));
      this.main.append(this.el('p', 'small', this.t('priceNotice')));
      this.main.append(this.button(this.t('contact'), () => this.go('contact'), 'text-button'));
      this.main.append(this.el('p', 'safety', this.t('safety')));
      this.loadResults();
    } else if (step === 'rental') {
      this.navigation('category'); this.heading('rentalTitle', 'rentalText');
      this.main.append(this.el('span', 'chip', this.t('rentalCategory', {category: this.t('category_' + category.id)})));
      this.main.append(this.storeLink(this.t('rentalAction'), STORE.rentalPath, 'action primary'));
      this.main.append(this.el('p', 'small', this.t('rentalNote')));
      this.contactLinks();
    } else if (step === 'contact') {
      this.navigation('home'); this.heading('teamTitle', 'teamText');
      this.contactLinks();
      this.main.append(this.el('p', 'address', STORE.address));
      this.main.append(this.el('p', 'safety', this.t('safety')));
      this.main.append(this.el('p', 'small', this.t('emergency')));
    }
    if (focus && this.dialog.open) this.focusHeading();
    this.main.scrollTop = 0;
  }
  async loadResults() {
    const requestId = ++this.requestId;
    this.abortController = new AbortController();
    this.main.setAttribute('aria-busy', 'true');
    this.status.textContent = this.t('loading');
    const result = await fetchCatalog(this.state.category.handles, {root: this.root, preview: this.preview, signal: this.abortController.signal});
    if (requestId !== this.requestId || this.state.step !== 'results' || !this.isConnected) return;
    this.main.removeAttribute('aria-busy');
    this.status.textContent = '';
    if (!result.products.length) {
      this.status.textContent = this.t('resultsUnavailable');
      this.resultList.append(this.el('p', 'notice', this.t('unavailable')));
      this.resultList.append(this.button(this.t('retry'), () => this.go('results'), 'text-button'));
      return;
    }
    const items = selectProducts(result.products, this.state.budget);
    this.status.textContent = this.t(items.length ? (result.failed ? 'resultsPartial' : 'resultsReady') : 'resultsNone', {count: items.length});
    if (!items.length) this.resultList.append(this.el('p', 'notice', this.t('noMatch')));
    else for (const product of items) this.resultList.append(this.productCard(product));
    this.resultList.append(this.el('p', 'checked', this.t(result.failed ? 'partial' : 'live')));
  }
  productCard(product) {
    const card = this.el('article', 'product');
    if (product.image) {
      const image = this.el('img', 'product-image'); image.src = product.image; image.alt = ''; image.loading = 'lazy'; image.width = 90; image.height = 90;
      image.addEventListener('error', () => image.remove(), {once: true}); card.append(image);
    }
    const info = this.el('div', 'product-info');
    info.append(this.el('h4', '', product.title));
    const formatted = money(product.price, this.currency, this.locale);
    info.append(this.el('p', 'price', !this.currency ? this.t('unknownCurrency') : formatted ? this.t(product.priceVaries ? 'from' : 'listed', {amount: formatted}) : this.t('seePrice')));
    if (!product.available) info.append(this.el('p', 'availability', this.t('notAvailable')));
    info.append(this.link(this.t('viewProduct'), productURL(product.handle, this.root), 'product-link'));
    card.append(info);
    return card;
  }
}

if (!customElements.get('medical-store-assistant')) customElements.define('medical-store-assistant', MedicalStoreAssistant);

})();
